import { fetchClient } from "@/api/api";
import type {
	CreateTransferMultiplierInput,
	CreateTransferMultiplierOutput,
	GetAllTransferMultipliersInput,
	GetAllTransferMultipliersOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import type { ApiError } from "@/utils/fetching";
import {
	type CustomMutationHook,
	type CustomQueryHook,
	invalidateQueries,
} from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { TRANSFER_MULTIPLIER_KEYS } from "./keys";

export const useGetTransferMultiplier: CustomQueryHook<
	GetAllTransferMultipliersOutput,
	GetAllTransferMultipliersInput
> = (payload, options) => {
	return useQuery<GetAllTransferMultipliersOutput, ApiError>({
		...options,
		queryKey: TRANSFER_MULTIPLIER_KEYS.useGetTransferMultiplier(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/transfer-multipliers/",
				{ throwOnError: true },
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useCreateTransferMultiplier: CustomMutationHook<
	CreateTransferMultiplierInput,
	CreateTransferMultiplierOutput
> = () => {
	const { toastApiError } = useApiError();
	return useMutation<
		CreateTransferMultiplierOutput,
		ApiError,
		CreateTransferMultiplierInput
	>({
		mutationKey: TRANSFER_MULTIPLIER_KEYS.useCreateTransferMultiplier(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/transfer-multipliers/",
				{ body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},

		onSuccess: () => {
			invalidateQueries(TRANSFER_MULTIPLIER_KEYS.useGetTransferMultiplier());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};
